import React, { useEffect, useRef, useState, ReactNode } from 'react';

interface FadeInSectionProps {
    children: ReactNode;
    delay?: number;
    direction?: 'up' | 'down' | 'left' | 'right' | 'none';
    duration?: number;
    threshold?: number;
    className?: string;
}

const FadeInSection: React.FC<FadeInSectionProps> = ({
    children,
    delay = 0,
    direction = 'up',
    duration = 0.8,
    threshold = 0.1,
    className = '',
}) => {
    const [isVisible, setIsVisible] = useState(false);
    const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
    const sectionRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        // Check for reduced motion preference
        const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
        setPrefersReducedMotion(mediaQuery.matches);

        const handleChange = (e: MediaQueryListEvent) => {
            setPrefersReducedMotion(e.matches);
        };

        mediaQuery.addEventListener('change', handleChange);
        return () => mediaQuery.removeEventListener('change', handleChange);
    }, []);

    useEffect(() => {
        // If user prefers reduced motion, show content immediately
        if (prefersReducedMotion) {
            setIsVisible(true);
            return;
        }

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                    // Optionally unobserve after animation to improve performance
                    if (sectionRef.current) {
                        observer.unobserve(sectionRef.current);
                    }
                }
            },
            {
                threshold,
                rootMargin: '0px 0px -50px 0px', // Trigger slightly before element enters viewport
            }
        );

        if (sectionRef.current) {
            observer.observe(sectionRef.current);
        }

        return () => {
            if (sectionRef.current) {
                observer.unobserve(sectionRef.current);
            }
        };
    }, [threshold, prefersReducedMotion]);

    const getTransformValue = () => {
        switch (direction) {
            case 'up':
                return 'translateY(40px)';
            case 'down':
                return 'translateY(-40px)';
            case 'left':
                return 'translateX(40px)';
            case 'right':
                return 'translateX(-40px)';
            case 'none':
                return 'translateY(0)';
            default:
                return 'translateY(40px)';
        }
    };

    return (
        <div
            ref={sectionRef}
            className={className}
            style={{
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? 'translate(0, 0)' : getTransformValue(),
                transition: prefersReducedMotion
                    ? 'none'
                    : `opacity ${duration}s ease-out ${delay}s, transform ${duration}s ease-out ${delay}s`,
                willChange: prefersReducedMotion ? 'auto' : 'opacity, transform',
            }}
        >
            {children}
        </div>
    );
};

export default FadeInSection;
